/* eslint-disable functional/prefer-readonly-type */
import React, { useState } from 'react'
import {
  Paper,
  Typography,
  IconButton,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Divider,
  Collapse,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import BarChartIcon from '@mui/icons-material/BarChart'
import ShowChartIcon from '@mui/icons-material/ShowChart'
import PieChartIcon from '@mui/icons-material/PieChart'
import InsertChartIcon from '@mui/icons-material/InsertChart'
import NumbersIcon from '@mui/icons-material/Numbers'
import { DataCombinationType, ChartType } from '~/interfaces/IStatistics'
import { useLang } from '~/hooks/useLang'

interface ChartItem {
  readonly id: string
  readonly dataCombination: DataCombinationType
  readonly chartType: ChartType
}

interface CustomDashboardChartsListProps {
  readonly charts: readonly ChartItem[]
  readonly onRemove: (id: string) => void
}

interface GroupedChart {
  readonly chartType: ChartType
  readonly charts:  ChartItem[]
}

const getChartIcon = (chartType: ChartType) => {
  const iconMap = {
    [ChartType.BAR]: <BarChartIcon fontSize="small" />,
    [ChartType.COLUMN]: <InsertChartIcon fontSize="small" />,
    [ChartType.PIE]: <PieChartIcon fontSize="small" />,
    [ChartType.LINE]: <ShowChartIcon fontSize="small" />,
    [ChartType.NUMERIC]: <NumbersIcon fontSize="small" />,
  }
  return iconMap[chartType] || <BarChartIcon fontSize="small" />
}

const getDataCombinationLabel = (dataCombination: DataCombinationType): string => {
  const labels: Record<DataCombinationType, string> = {
    [DataCombinationType.DISPATCH_BY_STATUS]: 'Total de Despachos por Status',
    [DataCombinationType.DEVICE_BY_STATUS]: 'Total de Dispositivos por Status',
    [DataCombinationType.DISPATCH_BY_TYPE]: 'Total de Despachos por Tipo',
    [DataCombinationType.DISPATCH_BY_SUBTYPE]: 'Total de Despachos por Subtipo',
    [DataCombinationType.DISPATCH_BY_CLOSED_STATUS]: 'Total de Despachos por Status de Fechamento',
    [DataCombinationType.DISPATCH_BY_DISPATCH_GROUP]: 'Total de Despachos por Grupo de Despacho',
    [DataCombinationType.DISPATCH_BY_CITY]: 'Total de Despachos por Cidade',
    [DataCombinationType.DISPATCH_BY_DISTRICT]: 'Total de Despachos por Bairro',
    [DataCombinationType.DISPATCH_BY_DEVICE]: 'Total de Despachos por Dispositivo',
    [DataCombinationType.DISPATCHES_BY_HOURS]: 'Total de Despachos por Intervalo de Hora',
    [DataCombinationType.DISPATCHES_BY_WEEKDAY]: 'Total de Despachos por Dia da Semana',
  }
  return labels[dataCombination]
}

const getChartTypeLabel = (chartType: ChartType): string => {
  const labels: Record<ChartType, string> = {
    [ChartType.NUMERIC]: 'Indicador Numérico',
    [ChartType.BAR]: 'Gráfico de Barras',
    [ChartType.COLUMN]: 'Gráfico de Colunas',
    [ChartType.PIE]: 'Gráfico Circular',
    [ChartType.LINE]: 'Gráfico de Linha',
  }
  return labels[chartType]
}

export default function CustomDashboardChartsList({
  charts,
  onRemove,
}: CustomDashboardChartsListProps) {
  const { translate } = useLang()
  const [expandedGroups, setExpandedGroups] = useState<readonly ChartType[]>([])

  if (charts.length === 0) {
    return null
  }

  // Agrupa os gráficos por chartType
  const groupedCharts: readonly GroupedChart[] = Object.values(
    charts.reduce((acc, chart) => {
      if (!acc[chart.chartType]) {
        acc[chart.chartType] = {
          chartType: chart.chartType,
          charts: [],
        }
      }
      acc[chart.chartType].charts.push(chart)
      return acc
    }, {} as Record<ChartType, GroupedChart>)
  )

  const handleToggleGroup = (chartType: ChartType) => {
    setExpandedGroups((prev) =>
      prev.includes(chartType)
        ? prev.filter((item) => item !== chartType)
        : [...prev, chartType]
    )
  }

  const removeAllChartsFromGroup = (chartType: ChartType) => {
    const chartsToRemove = charts.filter(
      (chart) => chart.chartType === chartType
    )
    chartsToRemove.forEach((chart) => onRemove(chart.id))
  }

  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        marginBottom: 3,
        marginX: 0,
      }}
    >
      <List
        component="nav"
        aria-labelledby="nested-list-charts"
        dense
        subheader={
          <>
            <ListSubheader component="div" id="nested-list-subheader">
              {translate('Gráficos Adicionados')}
              {' ('}
              {charts.length}
              {')'}
            </ListSubheader>
            <Divider />
          </>
        }
      >
        {groupedCharts.map((group) => (
          <React.Fragment key={group.chartType}>
            <ListItem
              secondaryAction={
                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={() => removeAllChartsFromGroup(group.chartType)}
                >
                  <DeleteIcon />
                </IconButton>
              }
              sx={{ paddingRight: 0 }}
            >
              <ListItemButton onClick={() => handleToggleGroup(group.chartType)}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
                  {getChartIcon(group.chartType)}
                </Box>
                <ListItemText
                  primary={translate(getChartTypeLabel(group.chartType))}
                />
                {group.charts.length > 0 &&
                  (expandedGroups.includes(group.chartType) ? (
                    <ExpandLess />
                  ) : (
                    <ExpandMore />
                  ))}
              </ListItemButton>
            </ListItem>
            <Collapse
              in={expandedGroups.includes(group.chartType)}
              timeout="auto"
              unmountOnExit
            >
              <List component="div" disablePadding dense>
                {group.charts.map((chart) => (
                  <ListItem
                    key={chart.id}
                    sx={{ pl: 8 }}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => onRemove(chart.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={translate(getDataCombinationLabel(chart.dataCombination))}
                      sx={{ pl: 2 }}
                    />
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </React.Fragment>
        ))}
      </List>
      <Box sx={{ padding: 2, backgroundColor: 'action.hover' }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          {translate('Os gráficos serão gerados quando você aplicar os filtros')}
        </Typography>
      </Box>
    </Paper>
  )
}
